import React, { useEffect, useRef, useState } from 'react';
import { Form, Input } from 'formik-antd';
import { Formik, FormikProps } from 'formik';
import { isEqual, isNil } from 'lodash';
import { Button, Col, Modal, Row } from 'antd';
import * as Yup from 'yup';

import {
  CreateWorkerModel,
  ResourceDownloadResponse,
  WorkerDetailViewModel,
} from 'api/backofficeApiClient';
import FormItem from 'components/formik/FormItem';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  getAllCities,
  getAllCountries,
  getAllCountryCities,
  getAllStates,
} from 'redux/reducers/regionSlice';
import SubmitButton from 'components/formik/SubmitButton/SubmitButton';
import { createWorker, updateWorker } from 'redux/reducers/workerSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import {
  ApiErrorMessage,
  WorkerAlreadyExistsError,
} from 'constants/ErrorMessages';
import { WorkerDetailAdminViewModel, WorkerTypeEnum } from 'api/adminApiClient';
import { clearFormError } from 'utils/formUtils';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import Radio from 'components/formik/Radio';
import InputNumber from 'components/formik/InputNumber';
import { getWorkerTypeLabel } from 'utils/workerUtils';
import WorkerUploader from './WorkerUpload';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { generateUploadFileList } from 'utils/fileUtils';
import { BaseUploadFile } from 'components/wrappers/BaseFileUpload';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import InternationalWorkerStateForm from './InternationalWorkerStateForm';
import WorkerStateForm from './WorkerStateForm';
import { countryUSAid } from 'constants/index';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

type Props = {
  onClose: () => void;
  onSuccess?: () => Promise<void>;
  isEditMode?: boolean;
  workerInfoFile?: ResourceDownloadResponse;
  worker?: WorkerDetailAdminViewModel | WorkerDetailViewModel;
};

const maxCharactersLength = 120;

export interface WorkerModalFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  workerType?: WorkerTypeEnum;
  country: number | null;
  state?: number;
  internationalCityName?: string;
  internationalStateName?: string;
  city: string;
  address: string;
  zip: string;
  taxId: number | null;
  workerFileKey: string[];
}

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxCharactersLength,
      `First name should be ${maxCharactersLength} characters maximum`,
    ),
  lastName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxCharactersLength,
      `Last name should be ${maxCharactersLength} characters maximum`,
    )
    .workerDuplicateCheck(),
  email: Yup.string()
    .trim()
    .email('Email format is invalid')
    .required('Field is required')
    .max(
      maxCharactersLength,
      `Email should be ${maxCharactersLength} characters maximum`,
    )
    .workerDuplicateCheck(),
  workerType: Yup.number().required('Field is required').workerDuplicateCheck(),
  phone: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'Phone number should be 120 characters maximum'),
  country: Yup.string().trim().required('Field is required'),
  state: Yup.string()
    .nullable()
    .trim()
    .when('country', {
      is: (country: number) => checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  city: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `City should be ${maxCharactersLength} characters maximum`,
    )
    .when('country', {
      is: (country: number) => checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  internationalStateName: Yup.string()
    .trim()
    .max(120, `State should be ${maxCharactersLength} characters maximum`)
    .when('country', {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  internationalCityName: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `City should be ${maxCharactersLength} characters maximum`,
    )
    .when('country', {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  address: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxCharactersLength,
      `Address should be ${maxCharactersLength} characters maximum`,
    ),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),
  taxId: Yup.number().nullable(),
  workerFileKey: Yup.array().of(Yup.string()),
});

const workerTypes = [
  {
    value: WorkerTypeEnum.W2,
    label: getWorkerTypeLabel(WorkerTypeEnum.W2),
  },
  {
    value: WorkerTypeEnum.IC,
    label: getWorkerTypeLabel(WorkerTypeEnum.IC),
  },
  {
    value: WorkerTypeEnum.InternationalIC,
    label: getWorkerTypeLabel(WorkerTypeEnum.InternationalIC),
  },
  {
    value: WorkerTypeEnum.SC,
    label: getWorkerTypeLabel(WorkerTypeEnum.SC),
  },
];

export default function WorkerModal(props: Props) {
  const { onClose, worker, isEditMode, onSuccess, workerInfoFile } = props;
  const dispatch = useAppDispatch();
  const [workerFileKeyInfo, setWorkerFileKeyInfo] = useState<BaseUploadFile[]>(
    workerInfoFile ? generateUploadFileList(workerInfoFile) : [],
  );
  const isAdmin = useAppSelector(isAdminSelector);

  useModalCloseOnNavigate(onClose);

  const formRef: any = useRef();

  const initialValues: WorkerModalFormValues = {
    firstName: worker?.workerInfo?.firstName || '',
    lastName: worker?.workerInfo?.lastName || '',
    email: worker?.workerInfo?.email || '',
    phone: worker?.workerInfo?.phoneNumber || '',
    workerType: worker?.workerType || undefined,
    country: worker?.location?.countryId || countryUSAid,
    state: worker?.location?.stateId || undefined,
    internationalStateName:
      worker?.location?.internationalStateName || undefined,
    internationalCityName: worker?.location?.internationalCityName || undefined,
    city: worker?.location?.city || '',
    address: worker?.location?.address || '',
    zip: worker?.location?.zipCode || '',
    taxId: !isNil(worker?.workerInfo?.taxId)
      ? (worker?.workerInfo?.taxId as number)
      : null,
    workerFileKey: worker?.workerInfo?.workerFileKey
      ? [worker?.workerInfo?.workerFileKey]
      : [],
  };

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getAllCountryCities());
    dispatch(getAllStates());
    dispatch(getAllCities());
  }, [dispatch]);

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Changes won’t be saved. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const handleSubmit = async (values: WorkerModalFormValues) => {
    // Prevent form submission if there were no changes.
    // https://mybasepay.atlassian.net/browse/MBO-2778
    if (isEditMode && isEqual(initialValues, values)) {
      BaseAlert.success('Changes successfully saved.');
      return onClose();
    }

    const {
      firstName,
      lastName,
      email,
      phone,
      workerType,
      city,
      state,
      address,
      zip,
      workerFileKey,
      taxId,
      internationalStateName,
      internationalCityName,
      country,
    } = values;

    const data: CreateWorkerModel = {
      workerInfo: {
        firstName,
        lastName,
        email,
        phoneNumber: phone,
        taxId: !isNil(taxId) ? taxId : undefined,
        workerFileKey: workerFileKey.length ? workerFileKey[0] : undefined,
      },
      workerType: workerType,
      location: {
        internationalCityName: !isNil(internationalCityName)
          ? internationalCityName
          : undefined,
        internationalStateName: !isNil(internationalStateName)
          ? internationalStateName
          : undefined,
        countryId: !isNil(country) ? country : undefined,
        address: !isNil(address) ? address : undefined,
        city: !isNil(city) ? city : undefined,
        stateId: !isNil(state) ? state : undefined,
        zipCode: !isNil(zip) ? zip : undefined,
      },
    };

    if (isEditMode) {
      await handleUpdateWorker(data, worker?.id as string);
    } else {
      await handleCreateWorker(data);
    }
  };

  const handleCreateWorker = async (data: CreateWorkerModel) => {
    const result = await dispatch(createWorker(data));
    if (createWorker.fulfilled.match(result)) {
      BaseAlert.success('New worker has been created.');
      onClose();
      onSuccess && onSuccess();
    } else {
      handleSubmitError(result);
    }
  };

  const handleUpdateWorker = async (
    data: CreateWorkerModel,
    workerId: string,
  ) => {
    const result = await dispatch(
      updateWorker({ data, workerId, isAdmin: isAdmin as boolean }),
    );

    if (updateWorker.fulfilled.match(result)) {
      onSuccess && (await onSuccess());
      BaseAlert.success('Changes successfully saved.');
      onClose();
    } else {
      handleSubmitError(result);
    }
  };

  const handleSubmitError = (result: any) => {
    if (result.payload?.errorMessage || result.payload?.errorDetails) {
      BaseAlert.error(result.payload?.errorMessage || ApiErrorMessage);

      if (!isNil(result.payload?.errorDetails?.isWorkerDuplicated)) {
        const workerInfoForm: FormikProps<WorkerModalFormValues> =
          formRef.current;
        workerInfoForm.setFieldValue('_formError', WorkerAlreadyExistsError);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleWorkerTypeChange = (
    formik: FormikProps<WorkerModalFormValues>,
  ) => {
    clearFormError(formik, '_formError');
    formik.setFieldTouched('taxId', false, false);
  };

  return (
    <BaseModal
      title={!isEditMode ? 'Create new worker' : 'Edit worker'}
      maxWidth={888}
      destroyOnClose
      open
      onCancel={handleCloseModal}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">Worker information</div>
              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <FormItem
                    data-id="workerFirstNameField"
                    label="First name *"
                    name="firstName"
                  >
                    <Input name="firstName" placeholder="Enter first name" autoComplete="off" />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    data-id="workerLastNameField"
                    label="Last name *"
                    name="lastName"
                  >
                    <Input
                      name="lastName"
                      placeholder="Enter last name"
                      onChange={() => clearFormError(formik, '_formError')}
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <FormItem
                    data-id="workerEmailField"
                    label="Email *"
                    name="email"
                  >
                    <Input
                      name="email"
                      placeholder="Enter email"
                      onChange={() => clearFormError(formik, '_formError')}
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    data-id="workerPhoneField"
                    label="Phone number *"
                    name="phone"
                  >
                    <Input name="phone" placeholder="Enter phone number" autoComplete="off" />
                  </FormItem>
                </Col>
              </Row>

              <div className="modal-title">Worker type *</div>

              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <FormItem data-id="workerTypeField" name="workerType">
                    <Radio.Group
                      name="workerType"
                      disabled={isEditMode}
                      onChange={() => handleWorkerTypeChange(formik)}
                      options={workerTypes}
                    />
                  </FormItem>
                </Col>
              </Row>

              {(formik.values.workerType === WorkerTypeEnum.InternationalIC ||
                formik.values.workerType === WorkerTypeEnum.IC) && (
                <>
                  <Col xs={24} md={12}>
                    <FormItem
                      data-id="workerTaxIdField"
                      label="Tax ID"
                      name="taxId"
                    >
                      <InputNumber name="taxId" placeholder="Enter Tax ID" />
                    </FormItem>
                  </Col>
                </>
              )}

              <div className="modal-title">Home address</div>

              {!checkIsCountriesWithStatesDropdown(formik.values.country) ? (
                <InternationalWorkerStateForm formik={formik} />
              ) : (
                <WorkerStateForm formik={formik} />
              )}

              {(formik.values.workerType === WorkerTypeEnum.InternationalIC ||
                formik.values.workerType === WorkerTypeEnum.IC) && (
                <>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <h5 className="modal-title">
                        Please use this section to upload worker's W9 Form
                      </h5>
                      <div className="upload-wrapper">
                        <FormItem
                          name="workerFileKey"
                          showValidateError={false}
                        >
                          <WorkerUploader
                            isAdmin={isAdmin as boolean}
                            workerType={formik.values.workerType}
                            fieldName="workerFileKey"
                            onChange={setWorkerFileKeyInfo}
                            workerFiles={workerFileKeyInfo}
                          />
                        </FormItem>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            <BaseModalFooter>
              <div>
                <Button
                  data-id="closeNewWorkerModalBtn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <SubmitButton type="primary" data-id="submitNewWorkerBtn">
                  {!isEditMode ? 'Create' : 'Save'}
                </SubmitButton>
              </div>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
